import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { usePermissions, type Capability } from '@/lib/permissions';
import { postLoginDestination } from '@/lib/navigation';
import { homeFor, mayOpen } from '@/lib/roleRouting';
import { isOn, useFeatures, type FlagKey } from '@/lib/features';
import { installSessionHygiene } from '@/lib/session';
import { Spinner } from '@/components/ui';

/**
 * ROUTES ARE LAZY. This is the difference between splitting the bundle and actually
 * deferring it.
 *
 * vite.config already sets manualChunks (vendor / query / charts / motion), and that
 * looked like code splitting — but manualChunks only decides how modules are GROUPED,
 * never when they are FETCHED. With all nineteen pages statically imported here, every
 * one of those chunks sat in the entry graph, so the browser downloaded the lot before
 * first paint.
 *
 * Measured on the live login page — an email field, a password field and a button:
 *
 *   index-*.js    175.5 KB transfer / 589.2 KB decoded /  662ms
 *   charts-*.js   113.3 KB transfer / 423.5 KB decoded / 1294ms   <- recharts
 *   vendor-*.js    53.1 KB transfer / 153.7 KB decoded /  987ms
 *   motion-*.js    39.5 KB transfer / 112.0 KB decoded / 1004ms
 *   -------------------------------------------------------------
 *   total         456 KB transfer / 1,474 KB decoded, DCL 2,289ms
 *
 * Recharts is imported by exactly two pages, AnalyticsPage and DashboardPage. Nothing on
 * the login screen, the jobs list, the candidate pipeline or the entire candidate
 * assessment flow renders a chart, and all of them were paying 423 KB to parse it.
 *
 * LoginPage and AdminLayout stay eager on purpose: one is the cold-start destination for
 * every recruiter, the other is the shell every admin route renders inside, so deferring
 * either buys nothing and costs a round trip at exactly the wrong moment.
 */

// Eager: cold-start entry points.
import LoginPage from '@/pages/auth/LoginPage';
import AdminLayout from '@/components/layout/AdminLayout';

// Auth
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));

// Admin — DashboardPage and AnalyticsPage are the only recharts consumers.
const DashboardPage = lazy(() => import('@/pages/admin/DashboardPage'));
const CandidatesPage = lazy(() => import('@/pages/admin/candidates/CandidatesPage'));
const CandidateDetailPage = lazy(() => import('@/pages/admin/candidates/CandidateDetailPage'));
const ReportDetailPage = lazy(() => import('@/pages/admin/reports/ReportDetailPage'));
const AnalyticsPage = lazy(() => import('@/pages/admin/AnalyticsPage'));
const SettingsPage = lazy(() => import('@/pages/admin/SettingsPage'));
const JobPositionsPage = lazy(() => import('@/pages/admin/positions/JobPositionsPage'));
const JobPositionDetailPage = lazy(() => import('@/pages/admin/positions/JobPositionDetailPage'));
const JobBuilderWizard = lazy(() => import('@/pages/admin/positions/JobBuilderWizard'));
const TeamPage = lazy(() => import('@/pages/admin/team/TeamPage'));
const TestLibraryPage = lazy(() => import('@/pages/admin/library/TestLibraryPage'));
const SkillTestBuilderPage = lazy(() => import('@/pages/admin/library/SkillTestBuilderPage'));
const OfferEditorPage = lazy(() => import('@/pages/admin/offers/OfferEditorPage'));
const AccountPage = lazy(() => import('@/pages/admin/account/AccountPage'));
// Every panelist's pages (the Interviewer's whole app): My interviews and the interview room.
const MyInterviewsPage = lazy(() => import('@/pages/interviewer/MyInterviewsPage'));
const InterviewRoomPage = lazy(() => import('@/pages/interviewer/InterviewRoomPage'));
// Staff: the interview board, with its side panel and every dialog (and @dnd-kit, which
// loads only with it).
const InterviewBoardPage = lazy(() => import('@/pages/admin/interviews/InterviewBoardPage'));

// Candidate portal
const AssessmentEntryPage = lazy(() => import('@/pages/candidate/AssessmentEntryPage'));
const AssessmentPage = lazy(() => import('@/pages/candidate/AssessmentPage'));
const AssessmentCompletePage = lazy(() => import('@/pages/candidate/AssessmentCompletePage'));
const PublicApplyPage = lazy(() => import('@/pages/public/PublicApplyPage'));
const OfferPage = lazy(() => import('@/pages/candidate/OfferPage'));

/** Shown while a route chunk is in flight. Centred, so it does not shift the layout. */
const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-label="Loading">
    <Spinner />
  </div>
);

// Guards
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  if (!isAuthenticated) {
    // Carry the destination through the login round-trip, so following a link to a
    // specific report or job doesn't dump the user on the dashboard afterwards.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?next=${next}`} replace />;
  }
  return <>{children}</>;
};

const GuestRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  if (isAuthenticated) {
    // Must honour ?next= too. Signing in flips isAuthenticated, which re-renders this
    // guard — so a hardcoded dashboard redirect here fires first and overrides the
    // destination the login page was about to navigate to. With the role, so an
    // Interviewer's ?next= to a staff page lands on My interviews instead.
    return <Navigate to={postLoginDestination(location.search, user?.role)} replace />;
  }
  return <>{children}</>;
};

/**
 * Where this account lands: the dashboard for staff, as today; My interviews for an
 * Interviewer; with Interviews on, the account page for an account that may not view
 * results (no current staff role is one).
 */
function useHome(): string {
  const role = useAuthStore((s) => s.user?.role);
  const features = useFeatures();
  return homeFor(role, isOn(features, 'INTERVIEWS_ENABLED'));
}

/**
 * THE INTERVIEWER'S DOOR (lib/roleRouting.ts). Wraps everything under /admin, so it runs
 * before the layout and before any page — and anything a page would fetch — loads. An
 * Interviewer at any address not on the allow-list is sent to My interviews; nobody else
 * is affected. Always on: it concerns only the new role, as the server's lock does.
 */
const RoleGate = ({ children }: { children: React.ReactNode }) => {
  const role = useAuthStore((s) => s.user?.role);
  const { pathname } = useLocation();
  if (!mayOpen(role, pathname)) return <Navigate to={homeFor(role)} replace />;
  return <>{children}</>;
};

/** The root and every unknown address: this account's home. */
const HomeRedirect = () => {
  const home = useHome();
  return <Navigate to={home} replace />;
};

/**
 * Gate a route by capability; a role that lacks it goes to its home (the dashboard for
 * staff, as before).
 *
 * `whenOn`: the gate applies only with that switch on, and otherwise lets everyone through
 * as today. Until the switches have loaded, a role the gate would refuse sees the loading
 * spinner rather than being sent away on the all-off default.
 *
 * `orCap`: a second capability that also opens the page — with the `orWhenOn` switch on.
 */
const RequireCap = ({ cap, children, whenOn, orCap, orWhenOn }: {
  cap: Capability; children: React.ReactNode; whenOn?: FlagKey; orCap?: Capability; orWhenOn?: FlagKey;
}) => {
  const { can } = usePermissions();
  const features = useFeatures();
  const home = useHome();
  if (can(cap)) return <>{children}</>;
  if (whenOn && !isOn(features, whenOn) && features.ready) return <>{children}</>;
  if (orCap && can(orCap) && (!orWhenOn || isOn(features, orWhenOn))) return <>{children}</>;
  // Undecided until the switches are in: neither show the page nor send them away.
  const undecided = !features.ready && (whenOn !== undefined || (orCap !== undefined && can(orCap)));
  if (undecided) return <RouteFallback />;
  return <Navigate to={home} replace />;
};

/**
 * Empty the query cache whenever the signed-in account changes (lib/session.ts; switch
 * "Refresh sign-in", and always for an Interviewer). Installed here, at the top, so it is
 * in place on every page, the sign-in page included.
 */
function useSessionHygiene() {
  const queryClient = useQueryClient();
  useEffect(() => installSessionHygiene(queryClient), [queryClient]);
}

export default function App() {
  useSessionHygiene();
  return (
    // reducedMotion="user" honours the OS "reduce motion" setting across every
    // framer-motion animation in the app. Beyond being the accessible default, it
    // removes the entrance fades entirely for users who ask for that — the same
    // fades that made the dashboard's text measure as low-contrast mid-animation.
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
        {/* Root redirect — each account's own home */}
        <Route path="/" element={<HomeRedirect />} />

        {/* Auth routes */}
        <Route path="/auth/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/auth/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
        <Route path="/auth/reset-password" element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />

        {/* Admin routes. RoleGate is the Interviewer's door: it runs before the layout and
            before any page below, so nothing they may not open is ever loaded or fetched. */}
        <Route path="/admin" element={<ProtectedRoute><RoleGate><AdminLayout /></RoleGate></ProtectedRoute>}>
          <Route index element={<HomeRedirect />} />
          <Route path="dashboard" element={<RequireCap cap="viewResults" whenOn="INTERVIEWS_ENABLED"><DashboardPage /></RequireCap>} />

          {/* Test Library — browse the catalog & author custom skill tests */}
          <Route path="library" element={<TestLibraryPage />} />
          <Route path="library/new" element={<RequireCap cap="manageLibrary"><SkillTestBuilderPage /></RequireCap>} />
          <Route path="library/:id/edit" element={<RequireCap cap="manageLibrary"><SkillTestBuilderPage /></RequireCap>} />

          {/* Jobs — a job bundles an assessment; the wizard builds it */}
          <Route path="positions" element={<RequireCap cap="viewResults" whenOn="INTERVIEWS_ENABLED"><JobPositionsPage /></RequireCap>} />
          <Route path="positions/new" element={<RequireCap cap="manageAssessments"><JobBuilderWizard /></RequireCap>} />
          <Route path="positions/:id/edit" element={<RequireCap cap="manageAssessments"><JobBuilderWizard /></RequireCap>} />
          <Route path="positions/:id" element={<RequireCap cap="viewResults" whenOn="INTERVIEWS_ENABLED"><JobPositionDetailPage /></RequireCap>} />

          {/* Candidates */}
          <Route path="candidates" element={<RequireCap cap="viewResults" whenOn="INTERVIEWS_ENABLED"><CandidatesPage /></RequireCap>} />
          <Route path="candidates/:id" element={<RequireCap cap="viewResults" whenOn="INTERVIEWS_ENABLED"><CandidateDetailPage /></RequireCap>} />

          {/* Results */}
          <Route path="reports/:attemptId" element={<RequireCap cap="viewResults" whenOn="INTERVIEWS_ENABLED"><ReportDetailPage /></RequireCap>} />
          <Route path="offers/:offerId" element={<RequireCap cap="manageAssessments"><OfferEditorPage /></RequireCap>} />
          <Route path="analytics" element={<RequireCap cap="viewResults" whenOn="INTERVIEWS_ENABLED"><AnalyticsPage /></RequireCap>} />

          {/* Interviews. The board is staff's: HR runs it, Hiring Managers read it and decide
              there (decision 5); it is not on the Interviewer's allow-list, so RoleGate sends
              them home first. With Interviews off the page says so itself. My interviews is
              every panelist's list and the Interviewer's home; the interview room is one
              round, for whoever sits on its panel (an Interviewer, or HR or a Hiring Manager
              on a panel) — staff who are not on it are taken to the board's card. Both are on
              the Interviewer's allow-list (lib/roleRouting.ts), open to every role, and ask
              the server for nothing about interviews while Interviews is off. */}
          <Route path="interviews" element={<RequireCap cap="viewResults" whenOn="INTERVIEWS_ENABLED"><InterviewBoardPage /></RequireCap>} />
          <Route path="my-interviews" element={<MyInterviewsPage />} />
          <Route path="interviews/rounds/:roundId" element={<InterviewRoomPage />} />

          {/* Your account: details, and Change password with "Team rules" on. Every role. */}
          <Route path="account" element={<AccountPage />} />

          {/* Team & settings — managers; with "Team rules" on, HR may add Interviewers */}
          <Route path="team" element={<RequireCap cap="manageTeam" orCap="addInterviewers" orWhenOn="TEAM_RULES"><TeamPage /></RequireCap>} />
          <Route path="settings" element={<RequireCap cap="manageSettings"><SettingsPage /></RequireCap>} />
        </Route>

        {/* Candidate portal — public routes */}
        <Route path="/assessment/:token" element={<AssessmentEntryPage />} />
        <Route path="/assessment/:token/start" element={<AssessmentPage />} />
        <Route path="/assessment/:token/complete" element={<AssessmentCompletePage />} />

        {/* A candidate's offer — private link, no login */}
        <Route path="/offer/:token" element={<OfferPage />} />

        {/* Public careers / self-serve apply */}
        <Route path="/apply/:jobId" element={<PublicApplyPage />} />

        {/* 404 — this account's home */}
        <Route path="*" element={<HomeRedirect />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </MotionConfig>
  );
}
